import { apiHelpers } from '@/services/api';
import type {
  Expense,
  ExpenseFilters,
  CreateExpenseDto,
  UpdateExpenseDto,
  PaginatedExpensesResponse,
  UserBalance,
  Balance,
  ExpenseSplit,
  SettleUpDto,
} from '@/types/expense.types';

// Expense Service
export const expenseService = {
  /**
   * Get list of expenses with filters
   */
  async getExpenses(filters?: ExpenseFilters): Promise<PaginatedExpensesResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.category) params.category = filters.category;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.paidBy) params.paidBy = filters.paidBy;
    }

    return apiHelpers.get<PaginatedExpensesResponse>('/expenses', params);
  },

  /**
   * Get single expense by ID
   */
  async getExpenseById(id: string): Promise<Expense> {
    return apiHelpers.get<Expense>(`/expenses/${id}`);
  },

  /**
   * Create a new expense
   */
  async createExpense(data: CreateExpenseDto): Promise<Expense> {
    return apiHelpers.post<Expense>('/expenses', data);
  },

  /**
   * Update an expense
   */
  async updateExpense(id: string, data: UpdateExpenseDto): Promise<Expense> {
    return apiHelpers.patch<Expense>(`/expenses/${id}`, data);
  },

  /**
   * Delete an expense
   */
  async deleteExpense(id: string): Promise<void> {
    return apiHelpers.delete(`/expenses/${id}`);
  },

  /**
   * Mark a split as paid
   */
  async markSplitAsPaid(expenseId: string, userId: string): Promise<ExpenseSplit> {
    return apiHelpers.post<ExpenseSplit>(`/expenses/${expenseId}/splits/${userId}/pay`);
  },

  /**
   * Get all balances (who owes whom)
   */
  async getBalances(): Promise<Balance[]> {
    const response = await apiHelpers.get<{ balances: Balance[]; debts: Balance[]; totalUnsettled: number }>('/expenses/balances');
    return response.balances;
  },

  /**
   * Get my balance summary
   */
  async getMyBalance(): Promise<UserBalance> {
    return apiHelpers.get<UserBalance>('/expenses/my-balance');
  },

  /**
   * Settle up with a user (record a payment)
   */
  async settleUp(data: SettleUpDto): Promise<void> {
    return apiHelpers.post('/expenses/settle-up', data);
  },
};

export default expenseService;
